import React, { useState, useEffect } from 'react';
import { adminApi, superAdminService } from '../../services/api';
import { invalidateSystemFlagsCache } from '../../config/useSystemFlags';
import { getAppMarket } from '../../services/MarketService';

interface PlanGateSetting {
    enabled: boolean;
    description: string;
    lastUpdatedAt?: string;
    updatedBy?: string;
}

export default function SystemSettingsPanel() {
    const [planGateEnabled, setPlanGateEnabled] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [updatedBy, setUpdatedBy] = useState<string | null>(null);

    useEffect(() => {
        fetchPlanGateSetting();
    }, []);

    const fetchPlanGateSetting = async () => {
        try {
            setLoading(true);
            const res = await adminApi.get('/api/super-admin/system/plan-gate');
            if (res.data?.success && res.data?.data) {
                setPlanGateEnabled(res.data.data.enabled);
                setLastUpdated(res.data.data.lastUpdatedAt);
                setUpdatedBy(res.data.data.updatedBy);
            }
        } catch (error) {
            console.error('[SystemSettings] Fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center mb-2">
                    <i className="fas fa-sliders-h text-indigo-600 mr-3"></i>
                    System Settings
                </h2>
                <p className="text-sm text-gray-600">Global configuration for subscription and pricing features</p>
            </div>

            {loading ? (
                <div className="flex flex-col items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent mb-2"></div>
                    <p className="text-sm text-gray-500 italic">Loading system settings...</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Plan Gate - PERMANENTLY LOCKED ON */}
                    <div className="flex items-center justify-between p-6 rounded-xl border-2 bg-emerald-50 border-emerald-200">
                        <div className="flex-1">
                            <h3 className="text-lg font-bold mb-1 text-emerald-900">
                                <i className="fas fa-lock mr-2"></i>
                                Trial Plan Gate
                            </h3>
                            <p className="text-sm text-gray-700 mb-2">
                                ✓ Trial users are restricted from accessing premium features (evictions, agreements, finance, communications, AML, analysis)
                            </p>
                            <div className="text-xs text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                                <i className="fas fa-shield-check"></i>
                                PERMANENTLY ENABLED — This setting cannot be disabled
                            </div>
                            {lastUpdated && (
                                <div className="text-xs text-gray-600 pt-2 border-t border-current border-opacity-20">
                                    <p>Last updated: {new Date(lastUpdated).toLocaleString()}</p>
                                    {updatedBy && <p>Updated by: {updatedBy}</p>}
                                </div>
                            )}
                        </div>

                        <div className="relative inline-flex h-10 w-16 flex-shrink-0 rounded-full bg-emerald-500 border-2 border-transparent cursor-not-allowed" title="Plan Gate is permanently enabled">
                            <span className="pointer-events-none inline-block h-9 w-9 transform rounded-full bg-white shadow ring-0 translate-x-6" />
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <i className="fas fa-info-circle text-blue-600 mt-1 mr-3 flex-shrink-0"></i>
                            <div className="text-sm text-blue-900">
                                <p className="font-semibold mb-1">How Trial Plan Gate Works:</p>
                                <ul className="space-y-1 ml-4 list-disc">
                                    <li>Trial users are restricted from accessing premium features</li>
                                    <li>Trial users see 🔒 lock icons in navigation for premium features</li>
                                    <li>API calls to premium endpoints return <code className="bg-white px-2 py-1 rounded">403 UPGRADE_REQUIRED</code></li>
                                    <li>Users can still access dashboard, properties, certificates, and tenants</li>
                                    <li><strong>This setting is permanently enabled and cannot be disabled</strong></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ──────────────────────────────────────────── */}
            {/*  PASSWORD RENEWAL POLICY (v2.5.4)             */}
            {/* ──────────────────────────────────────────── */}
            <PasswordPolicySection />

            {/* ──────────────────────────────────────────── */}
            {/*  SYSTEM FEATURE FLAGS (v2.5.6)                */}
            {/* ──────────────────────────────────────────── */}
            <SystemFlagsSection />
        </div>
    );
}

// ────────────────────────────────────────────────
// PASSWORD RENEWAL POLICY
// ────────────────────────────────────────────────
function PasswordPolicySection() {
    const [policy, setPolicy] = useState<{ maxAgeDays: number; warnDays: number; updatedAt: string | null; updatedBy: string | null } | null>(null);
    const [defaults, setDefaults] = useState<{ maxAgeDays: number; warnDays: number } | null>(null);
    const [overrideCount, setOverrideCount] = useState<number>(0);
    const [maxAgeInput, setMaxAgeInput] = useState<number>(0);
    const [warnInput, setWarnInput] = useState<number>(7);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<string | null>(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await superAdminService.getPasswordPolicy();
            const data: any = res.data;
            if (data?.success) {
                setPolicy(data.policy);
                setDefaults(data.defaults);
                setOverrideCount(typeof data.overrideCount === 'number' ? data.overrideCount : 0);
                setMaxAgeInput(data.policy?.maxAgeDays ?? 0);
                setWarnInput(data.policy?.warnDays ?? 7);
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load policy');
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (maxAgeInput < 0 || maxAgeInput > 3650) { setError('Max age must be 0–3650 days.'); return; }
        if (warnInput < 0 || warnInput > 365)     { setError('Warn days must be 0–365.'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await superAdminService.updatePasswordPolicy({ maxAgeDays: maxAgeInput, warnDays: warnInput });
            const data: any = res.data;
            if (data?.success) {
                setPolicy(data.policy);
                setSavedAt(new Date().toLocaleString('en-GB'));
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const enforced = (policy?.maxAgeDays ?? 0) > 0;

    return (
        <div className="mt-10 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center mb-2">
                <i className="fas fa-key mr-3 text-amber-600"></i>
                Password Renewal Policy
            </h2>
            <p className="text-gray-600 mb-4">
                Force users to rotate their Cognito password after N days. Set <code className="bg-gray-100 px-1 rounded">Max age</code> to <strong>0</strong> to disable enforcement.
                System admins, impersonation tokens, and SAML/SSO users are always exempt.
            </p>

            {overrideCount > 0 && (
                <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm">
                    <i className="fas fa-user-shield"></i>
                    <span><strong>{overrideCount}</strong> tenant{overrideCount === 1 ? '' : 's'} {overrideCount === 1 ? 'has' : 'have'} a per-tenant policy override.</span>
                    <span className="text-xs text-indigo-600">Manage from Users tab → policy icon on the row.</span>
                </div>
            )}

            {loading ? (
                <div className="text-gray-500">Loading…</div>
            ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Max password age (days)</label>
                            <input
                                type="number"
                                min={0}
                                max={3650}
                                value={maxAgeInput}
                                onChange={(e) => setMaxAgeInput(parseInt(e.target.value || '0', 10))}
                                className="w-full border border-amber-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">0 disables enforcement. Common values: 30, 60, 90.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">Warn N days before expiry</label>
                            <input
                                type="number"
                                min={0}
                                max={365}
                                value={warnInput}
                                onChange={(e) => setWarnInput(parseInt(e.target.value || '0', 10))}
                                className="w-full border border-amber-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">Frontend shows a warning when fewer than this many days remain.</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="text-sm">
                            <span className="font-semibold text-gray-700">Current:&nbsp;</span>
                            {enforced ? (
                                <span className="text-amber-800">
                                    Enforced — {policy?.maxAgeDays}-day max, warn at {policy?.warnDays}d
                                </span>
                            ) : (
                                <span className="text-gray-500">Disabled</span>
                            )}
                            {policy?.updatedAt && (
                                <span className="block text-xs text-gray-500 mt-1">
                                    Last changed: {new Date(policy.updatedAt).toLocaleString('en-GB')}{policy.updatedBy ? ` by ${policy.updatedBy}` : ''}
                                </span>
                            )}
                            {defaults && (
                                <span className="block text-xs text-gray-400 mt-1">
                                    Default: {defaults.maxAgeDays}-day max, warn at {defaults.warnDays}d
                                </span>
                            )}
                        </div>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save policy'}
                        </button>
                    </div>

                    {error && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
                    {savedAt && !error && <div className="mt-3 text-sm text-emerald-700">Saved at {savedAt}. New policy takes effect within 60 seconds (cache TTL).</div>}
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────
// SYSTEM FEATURE FLAGS (v2.5.6)
// One card, N toggles. Each row is a globally-scoped visibility switch.
// A missing entry = enabled (default). SA can flip individually.
// ────────────────────────────────────────────────

interface FlagDef { key: string; label: string; help: string; icon: string; }

const FLAG_REGISTRY: FlagDef[] = [
    { key: 'releaseFooter',        label: 'Release footer badge',   icon: 'fa-tag',           help: 'The "Release v… · date" line under the copyright in every footer (dashboard, marketing, legal pages).' },
    { key: 'prsDatabaseReadiness', label: 'PRS Database Readiness', icon: 'fa-database',      help: 'The traffic-light PRS scorecard widget shown at the top of the Properties list.' },
    { key: 'prsRegistration',      label: 'PRS Registration card',  icon: 'fa-id-card',       help: 'The per-property PRS Registration card on the Property Detail page (registration number + expiry).' },
    { key: 'prsSubmissionWizard',  label: 'PRS / Texas Registration Wizard',  icon: 'fa-wand-sparkles', help: 'The "PRS Submission Wizard" (Texas market: "Texas Registration") launch button on the Properties toolbar.' },
];

/**
 * F-308 — Super Admin must never present a PRS-named toggle to a Texas
 * operator. The readiness card is a PRS Database feature and is unlinked from
 * Texas entirely (so it is dropped here); the registration card and the wizard
 * survive under their Texas names.
 */
export const getFlagRegistry = (market: string): FlagDef[] => {
    if (market !== 'texas') return FLAG_REGISTRY;
    return FLAG_REGISTRY
        .filter((f) => f.key !== 'prsDatabaseReadiness')
        .map((f) => {
            if (f.key === 'prsRegistration') return {
                ...f,
                label: 'Rental Registration card',
                help: 'The per-property Rental Registration card on the Property Detail page (registration number + expiry).',
            };
            if (f.key === 'prsSubmissionWizard') return {
                ...f,
                label: 'Texas Registration Wizard',
                help: 'The "Texas Registration" launch button on the Properties toolbar and inside each property.',
            };
            return f;
        });
};

function SystemFlagsSection() {
    const [flags, setFlags] = useState<Record<string, boolean> | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [updatedBy, setUpdatedBy] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true); setError(null);
        try {
            const res = await superAdminService.getSystemFlags();
            const data: any = res.data;
            if (data?.success) {
                setFlags(data.data.flags || {});
                setUpdatedAt(data.data.updatedAt || null);
                setUpdatedBy(data.data.updatedBy || null);
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load system flags');
        } finally { setLoading(false); }
    };

    const toggle = async (key: string, currentEnabled: boolean) => {
        setSavingKey(key); setError(null);
        try {
            const res = await superAdminService.setSystemFlag(key, !currentEnabled);
            const data: any = res.data;
            if (data?.success) {
                setFlags(data.data.flags || {});
                setUpdatedAt(data.data.updatedAt || null);
                setUpdatedBy(data.data.updatedBy || null);
                invalidateSystemFlagsCache();
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Save failed');
        } finally { setSavingKey(null); }
    };

    return (
        <div className="mt-10 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center mb-2">
                <i className="fas fa-toggle-on mr-3 text-indigo-600"></i>
                Feature Visibility
            </h2>
            <p className="text-gray-600 mb-6">
                Global visibility toggles for advanced / commercially-protected features. A switch flipped
                off hides that feature across the platform for every tenant. Changes propagate within
                60 seconds (public cache TTL).
            </p>

            {loading ? (
                <div className="text-gray-500">Loading…</div>
            ) : (
                <div className="space-y-3">
                    {getFlagRegistry(getAppMarket()).map((def) => {
                        const enabled = flags?.[def.key] !== false; // default true
                        const saving = savingKey === def.key;
                        return (
                            <div
                                key={def.key}
                                className={`flex items-center justify-between p-5 rounded-xl border-2 ${enabled ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}
                            >
                                <div className="flex-1 pr-4">
                                    <h3 className={`text-base font-bold mb-1 ${enabled ? 'text-indigo-900' : 'text-gray-700'}`}>
                                        <i className={`fas ${def.icon} mr-2`}></i>
                                        {def.label}
                                        <span className={`ml-3 text-xs font-medium px-2 py-0.5 rounded-full ${enabled ? 'bg-indigo-200 text-indigo-800' : 'bg-gray-200 text-gray-700'}`}>
                                            {enabled ? 'Visible' : 'Hidden'}
                                        </span>
                                    </h3>
                                    <p className="text-sm text-gray-600">{def.help}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => toggle(def.key, enabled)}
                                    disabled={saving}
                                    aria-label={`Toggle ${def.label}`}
                                    className={`relative inline-flex h-9 w-14 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${enabled ? 'bg-indigo-500' : 'bg-gray-300'}`}
                                >
                                    <span className={`pointer-events-none inline-block h-8 w-8 transform rounded-full bg-white shadow ring-0 transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {updatedAt && (
                <div className="text-xs text-gray-500 mt-4">
                    Last changed: {new Date(updatedAt).toLocaleString('en-GB')}{updatedBy ? ` by ${updatedBy}` : ''}
                </div>
            )}
            {error && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
        </div>
    );
}
